import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { apiWithAuth } from '../../lib/api';
import toast from 'react-hot-toast';
import {
  ChevronLeftIcon,
  DocumentTextIcon,
  ArrowDownTrayIcon,
  CalendarIcon,
  EyeIcon,
  PrinterIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';

const TaxDocuments = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [documents, setDocuments] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const [printDoc, setPrintDoc] = useState(null); // 印刷対象

  useEffect(() => {
    fetchTaxDocuments();
  }, [selectedYear]);

  // refresh when reward is credited (employer marks completed)
  useEffect(() => {
    const onCredit = () => fetchTaxDocuments();
    if (typeof window !== 'undefined') window.addEventListener('wallet:credit', onCredit);
    return () => {
      if (typeof window !== 'undefined') window.removeEventListener('wallet:credit', onCredit);
    };
  }, [selectedYear]);

  // 印刷対象がセットされたらプリント実行
  useEffect(() => {
    if (printDoc) {
      // レイアウト描画を待ってから印刷
      setTimeout(() => {
        window.print();
      }, 100);
    }
  }, [printDoc]);

  const fetchTaxDocuments = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const client = apiWithAuth(token);
      const params = {};
      if (selectedYear) params.year = String(selectedYear);
      // Try multiple endpoints
      const endpoints = ['/tax-documents', '/users/tax-documents', '/user/tax-documents'];
      let docs = [];
      for (const ep of endpoints) {
        try {
          const res = await client.get(ep, { params });
          const d = res?.data;
          const arr = Array.isArray(d) ? d : (Array.isArray(d?.results) ? d.results : (Array.isArray(d?.data) ? d.data : null));
          if (arr) { docs = arr; break; }
        } catch (_) { /* try next */ }
      }
      // Fallback: build from wallet transactions grouped by employer for the selected year
      if (!Array.isArray(docs) || docs.length === 0) {
        try {
          const txRes = await client.get('/wallet/transactions');
          const td = txRes?.data;
          const txs = Array.isArray(td) ? td : (Array.isArray(td?.results) ? td.results : (Array.isArray(td?.data) ? td.data : []));
          // filter for selected year and positive income credits
          const yearTx = txs.filter(t => {
            const dt = new Date(t.created_at || t.date || Date.now());
            const typ = String(t.transaction_type || t.type || '').toLowerCase();
            const isIncomeType = ['reward', 'employer_payment', 'credit', 'income'].includes(typ);
            // amount fields can be in amount (gross), net_amount (net). Use any to decide sign
            const amt = Number(t.net_amount ?? t.amount ?? 0);
            return dt.getFullYear() === Number(selectedYear) && isIncomeType && amt > 0;
          });
          // group by employer
          const byEmployer = new Map();
          yearTx.forEach(t => {
            const key = t.employer_name || t.merchant || t?.meta?.employer_id || '支払者';
            const entry = byEmployer.get(key) || { total_income: 0, withholding_tax: 0, social_insurance: 0, issued_date: `${selectedYear}-12-31`, employer_name: key };
            const net = Math.max(0, Number(t.net_amount ?? t.amount ?? 0));
            const tax = Math.max(0, Number(
              t.tax_amount ??
              t.withholding_tax ??
              t?.meta?.withholding_tax ??
              t?.meta?.tax ??
              t?.meta?.fee ?? 0
            ));
            const gross = net + tax; // net + tax = 支払金額
            entry.total_income += gross;
            entry.withholding_tax += tax;
            byEmployer.set(key, entry);
          });
          docs = Array.from(byEmployer.entries()).map(([name, v], i) => ({
            id: `local-${selectedYear}-${i}`,
            type: '源泉徴収票',
            year: selectedYear,
            employer_name: name,
            total_income: Math.round(v.total_income),
            withholding_tax: Math.round(v.withholding_tax),
            social_insurance: Math.round(v.social_insurance || 0),
            issued_date: v.issued_date,
          }));
        } catch (_) { /* ignore */ }
      }
      if (!Array.isArray(docs) || docs.length === 0) {
        // No docs found
        setDocuments([]);
      }
      setDocuments(docs);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (documentId) => {
    try {
      const token = localStorage.getItem('token');
      const client = apiWithAuth(token);
      // Try backend-provided PDF
      const res = await client.get(`/tax-documents/${documentId}/download`, { responseType: 'blob' });
      if (res?.data) {
        const blob = res.data;
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `withholding_${documentId}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        toast.success('源泉徴収票をダウンロードしました');
        return;
      }
      throw new Error('no data');
    } catch (error) {
      // Fallback: generate minimal HTML file for local download (user can print to PDF)
      const doc = documents.find(d => d.id === documentId);
      if (!doc) { toast.error('ドキュメントが見つかりません'); return; }
      const html = `<!doctype html><html><head><meta charset="utf-8"><title>源泉徴収票(${doc.year})</title></head><body>
        <h1>源泉徴収票（${doc.year}年）</h1>
        <p>支払者: ${doc.employer_name}</p>
        <p>支払金額: ${doc.total_income.toLocaleString()} 円</p>
        <p>源泉徴収税額: ${doc.withholding_tax.toLocaleString()} 円</p>
        <p>社会保険料等: ${doc.social_insurance.toLocaleString()} 円</p>
        <p>発行日: ${doc.issued_date}</p>
        <hr/>
        <p>このファイルをブラウザで開き、印刷からPDFとして保存できます。</p>
      </body></html>`;
      const blob = new Blob([html], { type: 'text/html' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `withholding_${documentId}.html`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success('ローカル用の源泉徴収票ファイル（HTML）を生成しました');
    }
  };

  const handlePrint = async (documentId) => {
    try {
      const doc = documents.find((d) => d.id === documentId);
      if (!doc) {
        toast.error('印刷対象の源泉徴収票が見つかりません');
        return;
      }
      setPrintDoc(doc);
    } catch (error) {
      toast.error('印刷に失敗しました');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  const totalIncome = documents.reduce((sum, doc) => sum + doc.total_income, 0);
  const totalWithholdingTax = documents.reduce((sum, doc) => sum + doc.withholding_tax, 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">読み込み中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 印刷専用スタイル */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #print-area, #print-area * { visibility: visible; }
          #print-area { position: absolute; left: 0; top: 0; width: 100%; padding: 24px; }
          /* 用紙余白を最小化 */
          @page { size: A4; margin: 12mm; }
        }
      `}</style>

      {/* 印刷専用レイアウト */}
      {printDoc && (
        <div id="print-area" className="hidden print:block">
          <div className="border border-gray-400 rounded-md p-6">
            <h1 className="text-xl font-bold text-center mb-4">源泉徴収票（{printDoc.year}年）</h1>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-gray-600">受給者氏名</div>
                <div className="font-semibold">{user?.name || '（氏名）'}</div>
              </div>
              <div>
                <div className="text-gray-600">支払者名称</div>
                <div className="font-semibold">{printDoc.employer_name}</div>
              </div>
              <div>
                <div className="text-gray-600">支払金額</div>
                <div className="font-semibold">{printDoc.total_income.toLocaleString()} 円</div>
              </div>
              <div>
                <div className="text-gray-600">源泉徴収税額</div>
                <div className="font-semibold">{printDoc.withholding_tax.toLocaleString()} 円</div>
              </div>
              <div>
                <div className="text-gray-600">社会保険料等</div>
                <div className="font-semibold">{printDoc.social_insurance.toLocaleString()} 円</div>
              </div>
              <div>
                <div className="text-gray-600">発行日</div>
                <div className="font-semibold">{formatDate(printDoc.issued_date)}</div>
              </div>
            </div>

            <div className="mt-6 text-xs text-gray-600 leading-relaxed">
              <p>本書はローカル開発用に自動生成されたレイアウトです。実運用では事業者発行の正式な源泉徴収票をPDFで保存・印刷してください。</p>
            </div>
          </div>
        </div>
      )}
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <button
              onClick={() => navigate('/mypage')}
              className="mr-4 p-2 text-gray-600 hover:text-gray-900"
            >
              <ChevronLeftIcon className="w-6 h-6" />
            </button>
            <h1 className="text-xl font-bold text-gray-900">源泉徴収票（求職者向け）</h1>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 求職者向けインフォ */}
        <div className="bg-blue-50 border border-blue-200 text-blue-800 rounded-lg p-4 mb-6 text-sm">
          このページは求職者の方がご自身の源泉徴収票を確認・印刷するためのページです。勤務先（支払者）から発行された情報に基づきます。
        </div>
        {/* 年度選択 */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">年度を選択</h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {years.map((year) => (
              <button
                key={year}
                onClick={() => setSelectedYear(year)}
                className={`px-4 py-2 rounded-lg font-medium ${selectedYear === year
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
              >
                {year}年
              </button>
            ))}
          </div>
        </div>

        {/* 年間サマリー */}
        {documents.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{selectedYear}年 年間サマリー</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">{totalIncome.toLocaleString()}</div>
                <div className="text-sm text-gray-600">総支給額</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">{totalWithholdingTax.toLocaleString()}</div>
                <div className="text-sm text-gray-600">源泉徴収税額</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">{documents.length}</div>
                <div className="text-sm text-gray-600">支払先数</div>
              </div>
            </div>
          </div>
        )}

        {/* 源泉徴収票一覧 */}
        <div className="space-y-4">
          {documents.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm p-12 text-center">
              <DocumentTextIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {selectedYear}年の源泉徴収票がありません
              </h3>
              <p className="text-gray-600 mb-6">
                該当年度の勤務実績がないか、まだ源泉徴収票が発行されていません。
              </p>
              <div className="bg-blue-50 rounded-lg p-4 text-left">
                <h4 className="font-medium text-blue-900 mb-2">源泉徴収票について</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li> 源泉徴収票は毎年1月31日までに発行されます</li>
                  <li> 年間所得が88,000円以上の場合に発行対象となります</li>
                  <li> 発行後はこちらのページからダウンロードできます</li>
                </ul>
              </div>
            </div>
          ) : (
            documents.map((document) => (
              <div key={document.id} className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <DocumentTextIcon className="w-6 h-6 text-blue-600 mr-3" />
                      <h3 className="text-lg font-semibold text-gray-900">
                        {document.type} ({document.year}年)
                      </h3>
                    </div>

                    <p className="text-gray-600 mb-4">勤務先（支払者）: {document.employer_name}</p>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div>
                        <div className="text-sm text-gray-600">支払金額</div>
                        <div className="font-semibold text-gray-900">
                          {document.total_income.toLocaleString()}
                        </div>
                      </div>

                      <div>
                        <div className="text-sm text-gray-600">源泉徴収税額</div>
                        <div className="font-semibold text-gray-900">
                          {document.withholding_tax.toLocaleString()}
                        </div>
                      </div>

                      <div>
                        <div className="text-sm text-gray-600">社会保険料等</div>
                        <div className="font-semibold text-gray-900">
                          {document.social_insurance.toLocaleString()}
                        </div>
                      </div>

                      <div>
                        <div className="text-sm text-gray-600">発行日</div>
                        <div className="font-semibold text-gray-900">
                          {formatDate(document.issued_date)}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex space-x-2 ml-6">
                    <button
                      onClick={() => handleDownload(document.id)}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                      title="ダウンロード"
                    >
                      <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
                      ダウンロード
                    </button>

                    <button
                      onClick={() => handlePrint(document.id)}
                      className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors flex items-center"
                      title="印刷"
                    >
                      <PrinterIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* 確定申告について */}
        <div className="mt-8 bg-amber-50 rounded-lg p-6">
          <div className="flex items-start">
            <InformationCircleIcon className="w-6 h-6 text-amber-600 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-amber-900 mb-2">確定申告について</h4>
              <div className="text-amber-800 text-sm space-y-2">
                <p>
                  <strong>確定申告が必要な場合：</strong>
                </p>
                <ul className="space-y-1 ml-4">
                  <li> 年間所得が20万円を超える場合</li>
                  <li> 複数の事業者から収入がある場合</li>
                  <li> 源泉徴収されていない収入がある場合</li>
                </ul>

                <p className="mt-3">
                  <strong>確定申告の期間：</strong> 毎年2月16日〜3月15日
                </p>

                <p className="mt-3">
                  詳細な税務相談は税理士または税務署にご相談ください。
                  国税庁の確定申告作成コーナーもご利用いただけます。
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* お問い合わせ */}
        <div className="mt-6 bg-blue-50 rounded-lg p-6">
          <div className="flex items-start">
            <DocumentTextIcon className="w-6 h-6 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-blue-900 mb-2">源泉徴収票に関するお問い合わせ</h4>
              <p className="text-blue-800 text-sm leading-relaxed mb-3">
                源泉徴収票の内容に疑問がある場合や、ダウンロードができない場合は、
                カスタマーサポートまでお問い合わせください。
              </p>
              <button
                onClick={() => navigate('/mypage/contact')}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm"
              >
                お問い合わせする
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaxDocuments;